/**
 * Service areas. One entry per area we cover, extracted from ServiceArea.jsx
 * on 2026-09-27 so adding a city is a one-file edit and scripts/routes.mjs can
 * read it without parsing a component.
 *
 * ── coverage: "own" | "network" ──────────────────────────────────────────────
 * "own"      we inspect it ourselves. Darwin and the NT areas. The page speaks
 *            in the first person: our team, our reports, our prices.
 * "network"  we capture the enquiry and hand it to a local inspector. The page
 *            says exactly that. It must NEVER claim we inspect there, quote our
 *            Darwin prices as if they were the local inspector's, or imply we
 *            have people on the ground. That would be misleading conduct under
 *            the Australian Consumer Law, and it is also the doorway-page
 *            pattern search engines demote.
 *
 * When an inspector subscribes and takes an area, set `operator` on that entry
 * to their trading name and flip `coverage` to "partner". The page then names
 * them. That is the whole change — routes, sitemap and llms.txt follow.
 *
 * ── FOR ANDY ─────────────────────────────────────────────────────────────────
 * Every `poolRule` is a claim about STATE LAW, researched from the sources
 * cited, not from your knowledge. They differ substantially between states and
 * they are the most citable thing on these pages, which also makes them the
 * most damaging to get wrong. Confirm each before promoting these pages.
 * The ACT entry has NO official source URL yet — that one especially.
 * ─────────────────────────────────────────────────────────────────────────────
 */

const OWN = "own";
const NETWORK = "network";

const regionsData = {
  // ─────────────────────────────────────────────── Northern Territory (ours)
  darwin: {
    name: "Darwin",
    state: "NT",
    stateName: "Northern Territory",
    coverage: OWN,
    geo: { lat: -12.4634, lng: 130.8456 },
    title: "Building Inspections in Darwin",
    subtitle: "Professional Property Inspection Services Across Darwin",
    heroImage: "/assets/img/service-area-hero.jpg",
    description:
      "Aussie Inspections provides comprehensive building and property inspection services throughout Darwin. As the Northern Territory's capital city, Darwin's unique tropical climate and housing stock require specialist knowledge that our experienced team delivers on every inspection.",
    suburbs: [
      "Darwin City", "Bayview", "Stuart Park", "Larrakeyah", "The Gardens",
      "Parap", "Fannie Bay", "East Point", "Ludmilla", "The Narrows",
      "Woolner", "Winnellie", "Berrimah", "Airport", "Marrara",
      "Millner", "Coconut Grove", "Rapid Creek", "Nightcliff", "Alawa",
      "Brinkin", "Nakara", "Wanguri", "Wagaman", "Moil",
      "Anula", "Malak", "Karama", "Leanyer", "Lee Point",
      "Muirhead", "Lyons", "Wulagi", "Tiwi", "Casuarina",
      "Jingili", "Sanderson", "Buffalo Creek",
    ],
    localInfo: {
      climate:
        "Darwin's tropical climate with distinct wet and dry seasons creates unique challenges for buildings. High humidity, cyclone seasons, and intense UV exposure require thorough inspections to identify moisture damage, rust, and weather-related wear.",
      housingStock:
        "The Darwin region features a mix of elevated tropical homes, modern apartments, and commercial properties. Many older homes were designed for the climate but may show wear from decades of tropical conditions.",
      commonIssues: [
        "Termite and pest activity (extremely common in Darwin)",
        "Moisture damage and mould from humidity",
        "Roof and gutter deterioration from cyclone seasons",
        "Rust and corrosion on metal fixtures",
        "Concrete cancer in older structures",
        "Drainage issues during wet season",
      ],
    },
    // VERIFY: NT — Swimming Pool Safety Act 2004 / Regulations 2004, two
    // standards, 1 Jan 2003 threshold. Same claim as the NT pool guide.
    poolRule: {
      summary:
        "The NT runs two barrier standards — the Modified Australian Standard and the Community Safety Standard. Which applies depends on the size of the property, whether the pool was installed before or after 1 January 2003, and whether the property has previously been sold, transferred or leased.",
      source: {
        label: "Pool fencing and barrier safety standards — NT Government",
        url: "https://nt.gov.au/property/homeowners-and-landlords/pool-or-spa/install-a-pool-or-spa/about-pool-fencing-and-barrier-safety-standards",
      },
    },
    stats: { inspections: "500+", experience: "Combined 15+ years", satisfaction: "100%" },
  },

  palmerston: {
    name: "Palmerston",
    state: "NT",
    stateName: "Northern Territory",
    coverage: OWN,
    geo: { lat: -12.4861, lng: 130.9833 },
    title: "Building Inspections in Palmerston",
    subtitle: "Professional Property Inspection Services Across Palmerston",
    heroImage: "/assets/img/service-area-hero.jpg",
    description:
      "Aussie Inspections covers the whole of Palmerston, the Territory's second city. Palmerston's housing stock is newer than Darwin's, but the same tropical conditions apply and the same defects turn up — just at a different stage of a building's life.",
    suburbs: [
      "Driver", "Durack", "Gray", "Moulden", "Woodroffe",
      "Bakewell", "Gunn", "Rosebery", "Bellamack", "Johnston",
      "Zuccoli", "Mitchell", "Farrar", "Yarrawonga", "Pinelands",
      "Archer", "Marlow Lagoon", "Palmerston City",
    ],
    localInfo: {
      climate:
        "Palmerston shares Darwin's wet and dry seasons, with the same humidity, monsoonal rainfall and UV load. Being slightly inland, it sees less direct salt-air corrosion than the coastal Darwin suburbs.",
      housingStock:
        "Largely built from the 1980s onward, with substantial newer estates in Bellamack, Zuccoli and Johnston. Slab-on-ground construction dominates, and many homes are still under or just out of builder warranty.",
      commonIssues: [
        "Termite activity, including in newer builds",
        "Slab edge damp and drainage falling back toward the house",
        "Defects in recently completed estate housing",
        "Air-conditioning condensate discharging against walls",
        "Wet season water ingress around windows and roof penetrations",
        "Deterioration of sealants and flashings",
      ],
    },
    poolRule: {
      summary:
        "The NT runs two barrier standards — the Modified Australian Standard and the Community Safety Standard. Which applies depends on the size of the property, when the pool was installed, and whether the property has previously been sold, transferred or leased.",
      source: {
        label: "Pool fencing and barrier safety standards — NT Government",
        url: "https://nt.gov.au/property/homeowners-and-landlords/pool-or-spa/install-a-pool-or-spa/about-pool-fencing-and-barrier-safety-standards",
      },
    },
    stats: { inspections: "500+", experience: "Combined 15+ years", satisfaction: "100%" },
  },

  litchfield: {
    name: "Litchfield",
    state: "NT",
    stateName: "Northern Territory",
    coverage: OWN,
    geo: { lat: -12.55, lng: 131.05 },
    title: "Building Inspections in Litchfield Region",
    subtitle: "Professional Property Inspection Services Across the Litchfield Municipality",
    heroImage: "/assets/img/service-area-hero.jpg",
    description:
      "Aussie Inspections covers the Litchfield municipality, where rural and semi-rural blocks bring a different set of questions: bores and tanks, sheds and outbuildings, long driveways, and structures that have been added over decades.",
    suburbs: [
      "Howard Springs", "Humpty Doo", "Berry Springs", "Coolalinga", "Girraween",
      "Herbert", "Virginia", "Bees Creek", "Freds Pass", "Knuckey Lagoon",
      "McMinns Lagoon", "Noonamah", "Southport", "Acacia Hills", "Livingstone",
      "Marrakai", "Middle Point", "Lambells Lagoon",
    ],
    localInfo: {
      climate:
        "The same tropical wet and dry seasons as Darwin, with heavier seasonal inundation on low-lying rural blocks and more exposure to bushfire in the dry.",
      housingStock:
        "Rural and semi-rural properties on large blocks. Extensive outbuildings, sheds, carports and patios, frequently added over many years and often without complete approval records.",
      commonIssues: [
        "Unapproved or undocumented structures — the most common finding out here",
        "Termite activity, including in outbuildings and fence lines",
        "Rainwater tanks, bores and septic systems in unknown condition",
        "Seasonal inundation and drainage on low-lying blocks",
        "Long-run roofing and shed structures showing corrosion",
        "Timber in ground contact around the property",
      ],
    },
    poolRule: {
      summary:
        "The NT runs two barrier standards. On larger rural blocks the standard that applies can differ from suburban Darwin, because property size is one of the factors that determines it.",
      source: {
        label: "Pool fencing and barrier safety standards — NT Government",
        url: "https://nt.gov.au/property/homeowners-and-landlords/pool-or-spa/install-a-pool-or-spa/about-pool-fencing-and-barrier-safety-standards",
      },
    },
    stats: { inspections: "500+", experience: "Combined 15+ years", satisfaction: "100%" },
  },

  wagait: {
    name: "Wagait Beach & Cox Peninsula",
    state: "NT",
    stateName: "Northern Territory",
    coverage: OWN,
    geo: { lat: -12.4333, lng: 130.7333 },
    title: "Building Inspections in Wagait Beach & Cox Peninsula",
    subtitle: "Professional Property Inspection Services Across the Cox Peninsula",
    heroImage: "/assets/img/service-area-hero.jpg",
    description:
      "Aussie Inspections travels to Wagait Beach, Mandorah, Dundee Beach and the wider Cox Peninsula. Coastal exposure and remoteness make these properties a different proposition, and the travel is factored into the quote up front.",
    suburbs: [
      "Wagait Beach", "Dundee Beach", "Mandorah", "Belyuen", "Cox Peninsula",
      "Dundee Downs", "Dundee Forest", "Bynoe Harbour", "Charlotte",
    ],
    localInfo: {
      climate:
        "Direct coastal exposure on top of the tropical wet and dry cycle. Salt-laden air is relentless on anything metal, and cyclone exposure is a live consideration.",
      housingStock:
        "A mix of permanent homes, holiday properties and owner-built dwellings. Many are elevated, many have been extended over time, and maintenance intervals are often longer because of the travel involved.",
      commonIssues: [
        "Severe salt-air corrosion of roof screws, brackets, gutters and fixings",
        "Cyclone tie-down and structural connections needing attention",
        "Owner-built and extended structures with incomplete approval records",
        "Termite activity in timber-framed and elevated construction",
        "Deferred maintenance on properties used seasonally",
        "Water supply and septic systems in unknown condition",
      ],
    },
    poolRule: {
      summary:
        "The NT's two-standard system applies here as it does across the Territory, with property size and the pool's installation date determining which standard is assessed against.",
      source: {
        label: "Pool fencing and barrier safety standards — NT Government",
        url: "https://nt.gov.au/property/homeowners-and-landlords/pool-or-spa/install-a-pool-or-spa/about-pool-fencing-and-barrier-safety-standards",
      },
    },
    stats: { inspections: "500+", experience: "Combined 15+ years", satisfaction: "100%" },
  },

  // ───────────────────────────────────── Capitals (network — NOT our own crew)
  sydney: {
    name: "Sydney",
    state: "NSW",
    stateName: "New South Wales",
    coverage: NETWORK,
    geo: { lat: -33.8688, lng: 151.2093 },
    title: "Building Inspections in Sydney",
    subtitle: "Tell us about the property and we'll match you with a local Sydney inspector",
    heroImage: "/assets/img/service-area-hero.jpg",
    description:
      "Sydney's housing stock spans 150 years, from sandstone terraces to 2020s apartment towers, and the defects that matter differ enormously between them. Tell us what you're buying and we'll pass your enquiry to an inspector who works that part of Sydney.",
    suburbs: [
      "Sydney CBD", "Surry Hills", "Newtown", "Bondi", "Randwick",
      "Marrickville", "Leichhardt", "Balmain", "Glebe", "Paddington",
      "Manly", "Mosman", "Chatswood", "North Sydney", "Ryde",
      "Parramatta", "Burwood", "Strathfield", "Bankstown", "Liverpool",
      "Blacktown", "Castle Hill", "Hornsby", "Penrith", "Sutherland",
      "Cronulla", "Hurstville", "Kogarah", "Auburn", "Epping",
    ],
    localInfo: {
      climate:
        "Temperate and humid, with heavy east-coast rainfall events and significant salt exposure along the coastal strip from Cronulla up through the Northern Beaches.",
      housingStock:
        "Victorian and Federation terraces, interwar bungalows, post-war brick veneer, and a very large stock of strata apartments — including a well-publicised cohort of 2010s towers with waterproofing and structural defect histories.",
      commonIssues: [
        "Balcony and bathroom waterproofing failure in strata apartments",
        "Rising damp and salt attack in sandstone and early brick construction",
        "Structural movement in terraces, particularly party walls",
        "Illegal or unapproved granny flats and garage conversions",
        "Asbestos in pre-1990 wet areas, eaves and fencing",
        "Stormwater and drainage inadequate for east-coast downpours",
      ],
    },
    // VERIFY: NSW — Swimming Pools Act 1992 + Swimming Pools Regulation 2018;
    // certificate of compliance required before sale or lease (from 29 Apr
    // 2016); pools must be on the NSW Swimming Pool Register.
    poolRule: {
      summary:
        "In NSW a pool or spa must be registered on the NSW Swimming Pool Register, and a property with a pool generally cannot be sold or leased without a certificate of compliance under the Swimming Pools Act 1992 and Swimming Pools Regulation 2018. This is stricter than most states — get it checked before you go to market.",
      source: {
        label: "NSW Swimming Pool Register — NSW Government",
        url: "https://www.swimmingpoolregister.nsw.gov.au/information",
      },
    },
    stats: null,
  },

  melbourne: {
    name: "Melbourne",
    state: "VIC",
    stateName: "Victoria",
    coverage: NETWORK,
    geo: { lat: -37.8136, lng: 144.9631 },
    title: "Building Inspections in Melbourne",
    subtitle: "Tell us about the property and we'll match you with a local Melbourne inspector",
    heroImage: "/assets/img/service-area-hero.jpg",
    description:
      "Melbourne sits on some of the most reactive clay soil in the country, and foundation movement is the defect that decides more purchases here than anything else. Tell us about the property and we'll pass your enquiry to an inspector who works that part of Melbourne.",
    suburbs: [
      "Melbourne CBD", "Carlton", "Fitzroy", "Richmond", "St Kilda",
      "Brunswick", "Northcote", "Preston", "Coburg", "Footscray",
      "Yarraville", "Sunshine", "Werribee", "Hawthorn", "Camberwell",
      "Box Hill", "Glen Waverley", "Doncaster", "Ringwood", "Essendon",
      "Moonee Ponds", "Brighton", "Bentleigh", "Dandenong", "Frankston",
      "Craigieburn", "Point Cook", "Pakenham", "Reservoir", "Williamstown",
    ],
    localInfo: {
      climate:
        "Temperate with a wide daily range and pronounced wet and dry cycles that drive seasonal soil movement. Cold, damp winters make condensation and mould a live issue in poorly ventilated housing.",
      housingStock:
        "Victorian and Edwardian terraces and weatherboards close in, interwar and post-war brick further out, and very large volume-builder estates on the urban fringe at Craigieburn, Point Cook and Pakenham.",
      commonIssues: [
        "Foundation movement and cracking from highly reactive clay soils",
        "Rising damp in solid-brick and bluestone-footed housing",
        "Condensation and mould from poor ventilation in cold months",
        "Volume-builder defects in fringe estate housing",
        "Stumps and subfloor deterioration in weatherboard homes",
        "Asbestos in pre-1990 construction",
      ],
    },
    // VERIFY: VIC — mandatory pool/spa registration with council, certificate
    // of barrier compliance lodged with registration, re-inspection every four
    // years.
    poolRule: {
      summary:
        "Victoria requires every pool and spa to be registered with the local council, with a certificate of barrier compliance lodged. Barriers must be re-inspected every four years and a new compliance certificate lodged each time.",
      source: {
        label: "Pool and spa registration requirements — Victorian Building Authority",
        url: "https://www.vba.vic.gov.au/consumers/swimming-pools/registration-requirements",
      },
    },
    stats: null,
  },

  brisbane: {
    name: "Brisbane",
    state: "QLD",
    stateName: "Queensland",
    coverage: NETWORK,
    geo: { lat: -27.4698, lng: 153.0251 },
    title: "Building Inspections in Brisbane",
    subtitle: "Tell us about the property and we'll match you with a local Brisbane inspector",
    heroImage: "/assets/img/service-area-hero.jpg",
    description:
      "Brisbane's timber-and-tin Queenslanders are beautiful and termite-prone in equal measure, and the subtropical climate is hard on everything else. Tell us about the property and we'll pass your enquiry to an inspector who works that part of Brisbane.",
    suburbs: [
      "Brisbane CBD", "Fortitude Valley", "New Farm", "West End", "Paddington",
      "Toowong", "Indooroopilly", "Kenmore", "The Gap", "Ashgrove",
      "Clayfield", "Nundah", "Chermside", "Aspley", "Sandgate",
      "Wynnum", "Bulimba", "Camp Hill", "Coorparoo", "Carindale",
      "Mount Gravatt", "Sunnybank", "Annerley", "Moorooka", "Inala",
      "Forest Lake", "Springfield", "Redcliffe", "Everton Park", "Mitchelton",
    ],
    localInfo: {
      climate:
        "Subtropical, with hot humid summers, intense storm and hail events, and a flood history that matters for any property near the river or a creek catchment.",
      housingStock:
        "Classic Queenslanders on timber stumps, post-war lowset brick, and substantial new development in the outer south-west. Elevated timber construction is common and brings its own inspection priorities.",
      commonIssues: [
        "Termite activity — Queensland is a high-risk state and timber construction is widespread",
        "Stump and subfloor deterioration in elevated Queenslanders",
        "Storm and hail damage to roofing",
        "Flood history and its consequences for a specific address",
        "Unapproved build-in under elevated houses",
        "Asbestos in pre-1990 construction",
      ],
    },
    // VERIFY: QLD — pool safety certificate from a licensed pool safety
    // inspector; valid 2 years non-shared / 1 year shared; QBCC Pool Safety
    // Register.
    poolRule: {
      summary:
        "Queensland pools must be on the QBCC Pool Safety Register, and a pool safety certificate is issued by a licensed pool safety inspector. Certificates are valid for two years for a non-shared (house) pool and one year for a shared pool such as an apartment complex.",
      source: {
        label: "Buy, sell or lease a property with a pool — QBCC",
        url: "https://www.qbcc.qld.gov.au/home-owner-hub/swimming-pools/buy-sell-lease-property-pool",
      },
    },
    stats: null,
  },

  perth: {
    name: "Perth",
    state: "WA",
    stateName: "Western Australia",
    coverage: NETWORK,
    geo: { lat: -31.9523, lng: 115.8613 },
    title: "Building Inspections in Perth",
    subtitle: "Tell us about the property and we'll match you with a local Perth inspector",
    heroImage: "/assets/img/service-area-hero.jpg",
    description:
      "Perth's sandy soils, limestone and salt-laden coastal winds produce a defect profile you won't see on the east coast. Tell us about the property and we'll pass your enquiry to an inspector who works that part of Perth.",
    suburbs: [
      "Perth CBD", "Northbridge", "Subiaco", "Leederville", "Mount Lawley",
      "Victoria Park", "South Perth", "Fremantle", "Cottesloe", "Claremont",
      "Nedlands", "Scarborough", "Joondalup", "Wanneroo", "Morley",
      "Bayswater", "Midland", "Ellenbrook", "Armadale", "Gosnells",
      "Cannington", "Canning Vale", "Booragoon", "Rockingham", "Baldivis",
      "Kwinana", "Butler", "Yanchep", "Bassendean", "Melville",
    ],
    localInfo: {
      climate:
        "Mediterranean — hot dry summers and wet winters. Strong salt-laden sea breezes along the coastal corridor, and long dry spells that move sandy and limestone-influenced ground.",
      housingStock:
        "Double-brick construction dominates, unusually for Australia, alongside limestone in older coastal areas and very large modern estates north and south along the coast.",
      commonIssues: [
        "Salt attack and fretting in limestone and older brickwork",
        "Corrosion of fixings and roof hardware in the coastal corridor",
        "Footing movement on sandy and mixed soils",
        "Reticulation and drainage faults, and their effect on footings",
        "Termite activity — present in WA and often underestimated",
        "Asbestos in pre-1990 construction",
      ],
    },
    // VERIFY: WA — local governments must inspect pool/spa safety barriers at
    // least once every four years.
    poolRule: {
      summary:
        "In WA the local council does the checking: local governments are required to inspect private pool and spa safety barriers at least once every four years. There is no owner-initiated certificate scheme of the kind NSW and Queensland run, so the barrier's condition between council visits is on the owner.",
      source: {
        label: "Swimming pool and spa safety barrier requirements — WA Government",
        url: "https://www.wa.gov.au/organisation/building-and-energy/swimming-pool-and-spa-safety-barrier-requirements",
      },
    },
    stats: null,
  },

  adelaide: {
    name: "Adelaide",
    state: "SA",
    stateName: "South Australia",
    coverage: NETWORK,
    geo: { lat: -34.9285, lng: 138.6007 },
    title: "Building Inspections in Adelaide",
    subtitle: "Tell us about the property and we'll match you with a local Adelaide inspector",
    heroImage: "/assets/img/service-area-hero.jpg",
    description:
      "Salt damp in Adelaide's stone cottages and footing movement on Adelaide Plains clay are the two findings that shape most purchases here. Tell us about the property and we'll pass your enquiry to an inspector who works that part of Adelaide.",
    suburbs: [
      "Adelaide CBD", "North Adelaide", "Norwood", "Unley", "Prospect",
      "Glenelg", "Brighton", "Henley Beach", "Semaphore", "Port Adelaide",
      "Woodville", "Findon", "Marion", "Mitcham", "Blackwood",
      "Burnside", "Magill", "Campbelltown", "Modbury", "Golden Grove",
      "Salisbury", "Elizabeth", "Gawler", "Payneham", "Walkerville",
      "Goodwood", "Mile End", "Hindmarsh", "Aldinga", "Seaford",
    ],
    localInfo: {
      climate:
        "Mediterranean, with hot dry summers and mild wet winters. The long dry drives significant seasonal soil movement, and the wet reveals what the dry has opened up.",
      housingStock:
        "Bluestone and sandstone cottages and villas, interwar bungalows, post-war brick, and newer estates north and south. Solid masonry without a damp-proof course is common in the older stock.",
      commonIssues: [
        "Salt damp in stone and early brick — a defining South Australian defect",
        "Footing movement and cracking on reactive Adelaide Plains clay",
        "Deteriorated or absent damp-proof courses in pre-war housing",
        "Roof timber and ceiling deflection in older cottages",
        "Asbestos in pre-1990 construction",
        "Drainage discharging against solid masonry walls",
      ],
    },
    // VERIFY: SA — no statutory requirement to inspect an EXISTING pool,
    // including on sale; owner remains responsible for meeting safety
    // requirements; councils inspect NEW pools after completion.
    poolRule: {
      summary:
        "South Australia is the outlier: there is no requirement to have an existing pool or spa inspected for compliance, and no inspection requirement when selling a property with one. The owner is still responsible for meeting the safety requirements, and councils inspect new pools after completion — so an unchecked older barrier is a real risk for a buyer.",
      source: {
        label: "Pool and spa safety — SA Government",
        url: "https://www.sa.gov.au/topics/housing/keeping-your-property-safe/pool-and-spa-safety",
      },
    },
    stats: null,
  },

  hobart: {
    name: "Hobart",
    state: "TAS",
    stateName: "Tasmania",
    coverage: NETWORK,
    geo: { lat: -42.8821, lng: 147.3272 },
    title: "Building Inspections in Hobart",
    subtitle: "Tell us about the property and we'll match you with a local Hobart inspector",
    heroImage: "/assets/img/service-area-hero.jpg",
    description:
      "Cold, damp and steep: Hobart's combination of heritage timber housing, hillside sites and a genuine winter makes condensation, rot and retaining walls the recurring findings. Tell us about the property and we'll pass your enquiry to an inspector who works that part of Hobart.",
    suburbs: [
      "Hobart CBD", "Battery Point", "Sandy Bay", "South Hobart", "West Hobart",
      "North Hobart", "New Town", "Lenah Valley", "Mount Stuart", "Taroona",
      "Kingston", "Blackmans Bay", "Margate", "Glenorchy", "Moonah",
      "Claremont", "Berriedale", "Bellerive", "Howrah", "Lindisfarne",
      "Rosny", "Lauderdale", "Sorell", "Bridgewater", "Brighton",
    ],
    localInfo: {
      climate:
        "Cool temperate with cold wet winters and a real frost season. Condensation is the dominant moisture mechanism here, rather than the driving rain that shapes northern housing.",
      housingStock:
        "Substantial Georgian and Victorian heritage stock, weatherboard cottages, and post-war brick, much of it on steep sites requiring retaining and stepped footings.",
      commonIssues: [
        "Condensation, mould and poor ventilation in cold months",
        "Timber rot in weatherboard and subfloor framing",
        "Retaining wall condition and site drainage on steep blocks",
        "Heritage fabric with deferred maintenance and unsympathetic repairs",
        "Inadequate insulation and resulting interstitial moisture",
        "Asbestos in pre-1990 construction",
      ],
    },
    // VERIFY: TAS — barrier required for pools/spas holding 300mm or more,
    // applying to pools installed from 1994 onward.
    poolRule: {
      summary:
        "In Tasmania a pool or spa holding water 300mm or more deep requires a safety barrier, a requirement applying to pools and spas installed from 1994 onward. Approval questions catch people out here even for temporary and inflatable pools.",
      source: {
        label: "Pool safety barriers — Consumer, Building and Occupational Services TAS",
        url: "https://cbos.tas.gov.au/topics/products-services/safety/pool-safety-barriers",
      },
    },
    stats: null,
  },

  canberra: {
    name: "Canberra",
    state: "ACT",
    stateName: "Australian Capital Territory",
    coverage: NETWORK,
    geo: { lat: -35.2809, lng: 149.13 },
    title: "Building Inspections in Canberra",
    subtitle: "Tell us about the property and we'll match you with a local Canberra inspector",
    heroImage: "/assets/img/service-area-hero.jpg",
    description:
      "Canberra has a frost season, a large stock of ex-government housing, and a loose-fill asbestos history that still shapes due diligence. Tell us about the property and we'll pass your enquiry to an inspector who works that part of Canberra.",
    suburbs: [
      "Civic", "Braddon", "Turner", "Lyneham", "Dickson",
      "Ainslie", "Watson", "Hackett", "Kingston", "Manuka",
      "Narrabundah", "Griffith", "Deakin", "Yarralumla", "Curtin",
      "Woden", "Phillip", "Mawson", "Weston", "Holder",
      "Belconnen", "Bruce", "Kaleen", "Gungahlin", "Harrison",
      "Casey", "Tuggeranong", "Wanniassa", "Kambah", "Molonglo",
    ],
    localInfo: {
      climate:
        "Cool inland climate with hot dry summers and genuinely cold, frosty winters. Freeze-thaw cycling and large seasonal temperature swings are hard on external finishes and mortar.",
      housingStock:
        "A very large cohort of mid-century ex-government housing, later brick veneer, and extensive new development in Gungahlin and Molonglo. Standardised designs mean recurring, predictable defects.",
      commonIssues: [
        "Loose-fill asbestos insulation history — the 'Mr Fluffy' cohort, which still affects due diligence on pre-1980 homes",
        "Frost and freeze-thaw damage to mortar, paving and external finishes",
        "Recurring defects in standardised ex-government housing designs",
        "Inadequate insulation and winter condensation",
        "Footing movement on reactive soils in some districts",
        "Defects in recent Gungahlin and Molonglo estate housing",
      ],
    },
    // VERIFY: ACT — from 1 May 2024 owners have four years to meet barrier
    // standards and obtain a compliance certificate; on sale or rent the
    // vendor/landlord must disclose pool status and provide the certificate if
    // one has been obtained.
    // NO OFFICIAL SOURCE URL FOUND YET — this one needs a gov.act.gov.au or
    // Access Canberra citation before it is relied on. Highest-priority verify.
    poolRule: {
      summary:
        "The ACT changed its rules from 1 May 2024: owners have four years to bring a barrier up to standard and obtain a compliance certificate, and on sale or rent the vendor or landlord must disclose the pool's status and provide the certificate if one has been obtained.",
      source: null,
    },
    stats: null,
  },
};

export default regionsData;
export const REGION_IDS = Object.keys(regionsData);
export const isNetwork = (r) => r && r.coverage !== "own";
